import { IngredientsContext } from "@/app/clientLayout";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { default as Error } from "@/components/ErrorMessage";
import { CurrentUser, UserContext } from "@/components/Page";
import PromptIfUnsaved from "@/components/PromptIfUnsaved";
import {
  combineUnits,
  stringIsValidNumber,
  stringToNumber,
  toAbsoluteAmount,
} from "@/lib/measurement";
import {
  CreateManyRecipesMutation,
  Exact,
  RecipeCreateWithoutSchoolInput,
  RecipeSectionCreateWithoutRecipeInput,
  Recipe_MadeInGroups,
  useCreateManyRecipesMutation,
} from "@/queries/autogenerate/graphql";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import toast from "react-hot-toast";
import { UseMutationExecute } from "urql";
import { IndividualRecipe } from "./IndividualRecipe";

export type CreateManyRecipesFnType = UseMutationExecute<
  CreateManyRecipesMutation,
  Exact<{
    schoolID: string;
    recipes: RecipeCreateWithoutSchoolInput | RecipeCreateWithoutSchoolInput[];
  }>
>;

export interface CreateRecipesProps {
  submitRecipe: (createManyRecipes: CreateManyRecipesFnType) => void;
}

const CreateRecipes: React.FC<CreateRecipesProps> = ({ submitRecipe }) => {
  const [{ fetching, error }, createManyRecipes] =
    useCreateManyRecipesMutation();

  // if (error) return <Error error={error} />;

  return (
    <>
      {error ? <Error error={error} /> : null}
      <button
        className="btn btn-primary"
        onClick={() => submitRecipe(createManyRecipes)}
        disabled={fetching}
      >
        {fetching ? <div className="spinner"></div> : null}
        Create Recipes
      </button>
    </>
  );
};

interface InnerProps {
  initialRecipes: PartialRecipe[];
  updateRecipes?: (newValue: PartialRecipe[]) => void;
  user: CurrentUser;
  onCompleted: (recipeIDs: { id: string; folderID?: string }[]) => void;
}

const FinalisePartialRecipeInner: React.FC<InnerProps> = ({
  initialRecipes,
  updateRecipes,
  user,
  onCompleted,
}) => {
  const [recipes, setRecipesBase] = useState(initialRecipes);
  const setRecipes: Dispatch<SetStateAction<PartialRecipe[]>> = (
    recipes: PartialRecipe[]
  ) => {
    setRecipesBase(recipes);
    if (updateRecipes) {
      updateRecipes(recipes);
    }
  };

  const { ingredients: defaultIngredients } = useContext(IngredientsContext);

  const submitRecipe = useCallback(
    (createManyRecipes: CreateManyRecipesFnType) => {
      if (!user.school) return;

      const {
        school: { requiresTaggedRecipes: requiresTags },
      } = user;

      if (requiresTags && recipes.some((r) => !r.tags.length)) {
        toast.error("Recipes must be given a tag");
        return;
      }

      if (
        recipes.some((r) =>
          r.ingredientSections.some((s) =>
            s.ingredients.some((i) => !i.selected)
          )
        )
      ) {
        const missingIngredients = recipes
          .reduce<PartialRecipe["ingredientSections"]>(
            (prev, curr) => [...prev, ...curr.ingredientSections],
            []
          )
          .reduce<PartialRecipe["ingredientSections"][number]["ingredients"]>(
            (prev, curr) => [...prev, ...curr.ingredients],
            []
          )
          .filter((ri) => !ri.selected);

        toast.error((t) => (
          <div className="space-y-3">
            <p className="font-medium">
              Ingredients have not been selected for:
            </p>
            <ul className="list-inside list-disc">
              {missingIngredients.map((ri) => (
                <li key={ri.uuid}>{ri.text}</li>
              ))}
            </ul>
            <button className="btn mx-0" onClick={() => toast.dismiss(t.id)}>
              Dismiss
            </button>
          </div>
        ));
        return;
      }

      if (
        recipes.some((r) =>
          r.ingredientSections.some((s) =>
            s.ingredients.some((i) => !stringIsValidNumber(String(i.amount)))
          )
        )
      ) {
        toast.error(
          (t) => (
            <div className="space-y-3">
              <p className="font-medium">Ingredient amounts are invalid for:</p>
              <ul className="list-inside list-disc">
                {recipes
                  .reduce<PartialRecipe["ingredientSections"]>(
                    (prev, curr) => [...prev, ...curr.ingredientSections],
                    []
                  )
                  .reduce<
                    PartialRecipe["ingredientSections"][number]["ingredients"]
                  >((prev, curr) => [...prev, ...curr.ingredients], [])
                  .filter((ri) => !stringIsValidNumber(String(ri.amount)))
                  .map((ri) => (
                    <li key={ri.uuid}>
                      {
                        defaultIngredients.find(
                          (ing) => ing.id == ri.selected?.id
                        )?.name
                      }
                    </li>
                  ))}
              </ul>
              <button className="btn mx-0" onClick={() => toast.dismiss(t.id)}>
                Dismiss
              </button>
            </div>
          ),
          {
            duration: 30 * 1000,
          }
        );
        return;
      }

      const tempNonExistentUnitIngredients = recipes
        .reduce<PartialRecipe["ingredientSections"]>(
          (prev, curr) => [...prev, ...curr.ingredientSections],
          []
        )
        .reduce<PartialRecipe["ingredientSections"][number]["ingredients"]>(
          (prev, curr) => [...prev, ...curr.ingredients],
          []
        )
        .filter((ri) => {
          const selectedIngredient = defaultIngredients.find(
            (ing) => ing.id == ri.selected?.id
          );
          if (!selectedIngredient) return false;

          const unit = combineUnits(selectedIngredient).find(
            (u) => u.id === ri.unitId
          );
          if (unit) return false;

          return true;
        });
      if (tempNonExistentUnitIngredients.length) {
        toast.error(
          (t) => (
            <div className="space-y-3">
              <p className="font-medium">
                Some ingredients have non-existent units. Please change the
                units:
              </p>
              <ul className="list-inside list-disc">
                {tempNonExistentUnitIngredients.map((ri) => (
                  <li key={ri.uuid}>
                    {defaultIngredients.find((ing) => ing.id == ri.selected?.id)
                      ?.name ?? ri.text}{" "}
                    - {ri.unitId}
                  </li>
                ))}
              </ul>
              <button className="btn mx-0" onClick={() => toast.dismiss(t.id)}>
                Dismiss
              </button>
            </div>
          ),
          { icon: <ExclamationTriangleIcon className="icon-lg icon-orange" /> }
        );
        return;
      }

      if (
        recipes.some((r) =>
          r.ingredientSections.some((s) => s.ingredients.some((i) => !i.amount))
        )
      ) {
        toast.error("Some ingredients have not been given an amount");
        return;
      }

      if (recipes.some((r) => r.public && !r.method)) {
        toast.error(
          "Recipes cannot be shared without a method. Please provide a method for all shared recipes."
        );
        return;
      }

      const folders = user.school?.recipeCollections ?? [];

      const newRecipes: RecipeCreateWithoutSchoolInput[] = recipes.map((r) => {
        let ingredientPosition = 1;

        // Boolean to know which sections is... sectionless
        const sections: [boolean, RecipeSectionCreateWithoutRecipeInput][] =
          r.ingredientSections.map<
            [boolean, RecipeSectionCreateWithoutRecipeInput]
          >(({ uuid, name, section, ingredients }, index) => [
            section,
            {
              id: uuid,
              name,
              position: index + 1,
              recipeIngredients: {
                create: ingredients
                  .map(({ uuid, selected, amount, unitId, preparation }) => {
                    const tempIngredient = defaultIngredients.find(
                      (ing) => ing.id == selected?.id
                    );
                    if (!tempIngredient) return;

                    const unit = combineUnits(tempIngredient).find(
                      (u) => u.id === unitId
                    );
                    if (!unit) return;

                    return {
                      id: uuid,
                      // recipe: section ? { connect: { id: r.uuid } } : undefined,
                      recipe: { connect: { id: r.uuid } },
                      position: ++ingredientPosition,
                      amount: toAbsoluteAmount(
                        stringToNumber(String(amount)),
                        unit,
                        defaultIngredients.find((ing) => ing.id == selected?.id)
                          ?.density ?? undefined,
                        r.servings
                      ),
                      preparation,
                      ingredient: {
                        connect: { id: selected?.id },
                      },
                      unit: { connect: { id: unitId } },
                    };
                  })
                  .filter((ri): ri is NonNullable<typeof ri> => !!ri),
              },
            },
          ]);

        return {
          id: r.uuid,
          name: r.name,
          source: r.source || undefined,
          inspiration: r.inspiration
            ? { connect: { id: r.inspiration } }
            : undefined,
          process: r.process,
          public: r.public,
          image: r.image || undefined,
          servings: r.servings || 1,
          madeInGroups: r.madeInGroups || undefined,
          method: r.method || undefined,
          recipeIngredients: {
            create: sections
              // sometimes multiple default sections are created
              // so we need to combine them
              .filter(([isSection]) => !isSection)
              .reduce<
                NonNullable<
                  NonNullable<
                    RecipeCreateWithoutSchoolInput["recipeIngredients"]
                  >["create"]
                >
              >(
                (prev, [section, sectionCreateInput]) => [
                  ...prev,
                  ...(sectionCreateInput.recipeIngredients?.create ?? []),
                ],
                [] as NonNullable<
                  NonNullable<
                    RecipeCreateWithoutSchoolInput["recipeIngredients"]
                  >["create"]
                >
              )
              .map((ri) => ({
                ...ri,
                recipe: undefined,
              })),
          },
          recipeSections: {
            create: sections
              .filter(([isSection]) => isSection)
              .map(([, section]) => section),
          },
          user: { connect: { id: user.id } },
          tags: {
            connect: r.tags
              .filter((t) => !t.__isNew__)
              .map((t) => ({ id: t.value })),
            create: r.tags
              .filter((t) => t.__isNew__)
              .map((t) => ({
                id: t.value,
                name: t.label,
                school: { connect: { id: user.school?.id } },
              })),
          },
          classes: r.classId
            ? {
                connect: [{ id: r.classId }],
              }
            : undefined,
          recipeInformation: r.recipeInformation
            ? { create: r.recipeInformation.map((contents) => ({ contents })) }
            : undefined,
          recipeCollection:
            r.folder && folders.some((f) => f.id === r.folder)
              ? { connect: { id: r.folder } }
              : undefined,
        };
      });

      if (newRecipes.length) {
        createManyRecipes({
          schoolID: user.school.id,
          recipes: newRecipes,
        }).then(({ error }) => {
          if (error) {
            toast.error(
              "There was an error creating the recipe: " + error.message
            );
            console.error(error);
          } else {
            onCompleted(
              recipes.map((r) => ({
                id: r.uuid,
                folderID: r.folder,
              }))
            );
          }
        });
      } else {
        toast.error("The recipes have no ingredients selected");
      }
    },
    [recipes]
  );

  return recipes.length ? (
    <>
      <PromptIfUnsaved dataUnsaved={!updateRecipes} />
      <div className="mx-auto max-w-2xl space-y-5">
        {recipes.map((recipe) => (
          <IndividualRecipe
            key={recipe.uuid}
            user={user}
            recipe={recipe}
            recipes={recipes}
            setRecipes={setRecipes}
          />
        ))}
      </div>
      <CreateRecipes submitRecipe={submitRecipe} />
    </>
  ) : (
    <p>There seems to be no data 😕</p>
  );
};

export type PartialRecipe = {
  uuid: string;
  name: string;
  folder?: string;
  public: boolean;
  source?: string | null;
  inspiration?: string;
  process: string;
  image?: string | null;
  servings: number;
  madeInGroups?: Recipe_MadeInGroups | null;
  method?: string | null;
  recipeInformation?: string[] | null;
  classId?: string | null;
  tags: readonly {
    value: string;
    label: string;
    __isNew__?: boolean;
  }[];
  ingredientSections: {
    uuid: string;
    name: string;
    section: boolean;
    ingredients: {
      uuid: string;
      text: string;
      name?: string | null;
      amount: number | string;
      preparation?: string | null;
      unitId?: string | null;
      derivedUnit: string;
      isChecked: boolean;
      tags: {
        value: string;
        label: string;
        __isNew__?: boolean;
      }[];
      selected?: {
        id: string;
      };
      options: {
        id: string;
        similar: boolean;
        similarity: number;
      }[];
    }[];
  }[];
};

interface Props {
  initialRecipes: PartialRecipe[];
  updateRecipes?: (newValue: PartialRecipe[]) => void;
  onCompleted: (recipeIDs: { id: string; folderID?: string }[]) => void;
}

const FinalisePartialRecipe: React.FC<Props> = ({
  initialRecipes,
  updateRecipes,
  onCompleted,
}) => {
  return (
    <UserContext.Consumer>
      {(user) => (
        <FinalisePartialRecipeInner
          {...{
            initialRecipes,
            updateRecipes,
            user,
            onCompleted,
          }}
        />
      )}
    </UserContext.Consumer>
  );
};

export default FinalisePartialRecipe;
